/**
 * Shared palm-rejection state, engine-agnostic.
 *
 * Both drawing engines arm themselves on the first Apple Pencil contact, and
 * both must remember it: a canvas remounts per question, so without persistence
 * the first strokes of every question are unprotected until the student happens
 * to touch the Pencil down again.
 *
 * Design: docs/FeatureDesigns/canvas-palm-rejection.md
 */

const PENCIL_SEEN_KEY = 'canvas.pencilSeen';
const PALM_REJECTION_KEY = 'canvas.palmRejection';

export type PalmRejectionSetting = 'auto' | 'off';

// localStorage throws in Safari private mode — a lost preference is nothing to
// break drawing over.
function readFlag(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeFlag(key: string, value: string): void {
    try {
        window.localStorage.setItem(key, value);
    } catch { /* ignore */ }
}

/** Has a stylus ever touched a canvas on this device? */
export function isPencilSeen(): boolean {
    return readFlag(PENCIL_SEEN_KEY) === '1';
}

export function markPencilSeen(): void {
    writeFlag(PENCIL_SEEN_KEY, '1');
}

export function getPalmRejectionSetting(): PalmRejectionSetting {
    return readFlag(PALM_REJECTION_KEY) === 'off' ? 'off' : 'auto';
}

/**
 * Every write to the setting, recorded for the ?canvasdebug=1 overlay.
 *
 * The setting has been observed flipping itself to 'off' on the first Pencil
 * stroke, which disarms palm rejection on a device that just armed it. The only
 * caller on the Fabric path is a toolbar button's onClick, so knowing WHERE the
 * write comes from is the whole diagnosis.
 */
export interface PalmWriteRecord {
    setting: PalmRejectionSetting;
    /** Caller frames, nearest first — the button handler, or something else. */
    from: string;
}

let palmWrites: PalmWriteRecord[] = [];

export function getPalmWrites(): PalmWriteRecord[] {
    return palmWrites;
}

export function clearPalmWrites(): void {
    palmWrites = [];
}

export function setPalmRejectionSetting(setting: PalmRejectionSetting): void {
    const frames = (new Error().stack ?? '')
        .split('\n')
        .slice(1, 6)
        .map(f => f.trim().replace(/^at\s+/, '').split(/[@(]/)[0].trim())
        .filter(f => f && f !== 'setPalmRejectionSetting');
    palmWrites = [...palmWrites, { setting, from: frames.slice(0, 3).join('<') }].slice(-6);
    writeFlag(PALM_REJECTION_KEY, setting);
}

/** Should touch input be ignored right now? Armed AND not overridden. */
export function isPalmRejectionActive(): boolean {
    return isPencilSeen() && getPalmRejectionSetting() === 'auto';
}
